import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Slider, { SliderThumb } from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import reactStringReplace from 'react-string-replace';

import InfoIcon from "../support_files/InfoIcon";
import ToggleButton from '@mui/material/ToggleButton';

import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import CoffeeIcon from '@mui/icons-material/Coffee';
import RamenDiningOutlinedIcon from '@mui/icons-material/RamenDiningOutlined';
import LocalBarIcon from '@mui/icons-material/LocalBar';

import Grow from '@mui/material/Grow';
import Zoom from '@mui/material/Zoom';
import Fade from '@mui/material/Fade';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';

function HelpIcon() {
  return (
    <>
      <svg width={0} height={0}>
        <linearGradient id="gradientColorsHelp2" x1={0} y1={1} x2={1} y2={0} gradientTransform="rotate(0)">
          <stop offset='0%' stopColor="#B9EFFF" />
          <stop offset='100%' stopColor="#F2F6F8" />
        </linearGradient>
      </svg>
      <HelpOutlineOutlinedIcon sx={{ fill: "url(#gradientColorsHelp2)", fontSize: "26px", display: "inline-block" }} />
    </>
  )
};

function GradientIcon(icon) {
  return (
    <>
      <svg width={0} height={0}>
        <linearGradient id="gradientColorsIcon" x1={0} y1={1} x2={1} y2={0} gradientTransform="rotate(0)">
          <stop offset='0%' stopColor="#008FF5" />
          <stop offset='100%' stopColor="#8ED4FF" />
        </linearGradient>
      </svg>
      {icon.activeIcon}
    </>
  )
};


function EstimateQuestion({ estimate, onChange, checked }) {

  function iOS() {
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
      || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  }

  const isIOS = iOS();

  function handleClick(e) {
    if (e.target.tagName === "INPUT") {
      e.target.select();
    }
  }

  const handleHelpToggle = () => {
    onChange({
      ...estimate,
      selectedHelp: !estimate.selectedHelp
    });
  };

  const replacedTitle = reactStringReplace((estimate.estimate), /(swipe yes|a match|a conversation|a first date|a second date|a third date)\b/, (match, i) => (
    <span key={i} style={{
      backgroundImage: "linear-gradient(.03turn, #0077BB, #80CEFF)", color: "transparent",
      backgroundClip: "text", whiteSpace: "nowrap"
    }}>{match}</span>
  ));

  const replacedBody = reactStringReplace((estimate.description), /(Foundational Criteria|Additional Criteria)\b/, (match, i) => (
    <span key={i} style={{
      fontWeight: 600
    }}>{match}</span>
  ));

  const helpDescriptionLines = estimate.helpDescription 
    ? estimate.helpDescription.split('\n').filter(line => line.trim())
    : [];

  const iconList = {
    "FavoriteBorderIcon": <GradientIcon key={estimate.estimate_id} activeIcon={<FavoriteBorderIcon sx={{ fill: "url(#gradientColorsIcon)" }} />} />
    , "FavoriteIcon": <GradientIcon key={estimate.estimate_id} activeIcon={<FavoriteIcon sx={{ fill: "url(#gradientColorsIcon)" }} />} />
    , "CommentIcon": <GradientIcon key={estimate.estimate_id} activeIcon={<CommentIcon sx={{ fill: "url(#gradientColorsIcon)" }} />} />
    , "CoffeeIcon": <GradientIcon key={estimate.estimate_id} activeIcon={<CoffeeIcon sx={{ fill: "url(#gradientColorsIcon)" }} />} />
    , "RamenDiningOutlinedIcon": <GradientIcon key={estimate.estimate_id} activeIcon={<RamenDiningOutlinedIcon sx={{ fill: "url(#gradientColorsIcon)" }} />} />
    , "LocalBarIcon": <GradientIcon key={estimate.estimate_id} activeIcon={<LocalBarIcon sx={{ fill: "url(#gradientColorsIcon)" }} />} />
  }

  const ReplacedIcon = reactStringReplace((estimate.icon), /(.*)/, (match, i) => {
    return (iconList[match])
  });

  const handleChange = (event, newValue) => {
    if (isIOS && event.type === "mousedown") {
      return;
    }
    
    onChange({
      ...estimate,
      value: newValue,
      touched: true
    });
  };

  return (
    <Fade
      in={checked}
      style={{
        transformOrigin: '0 0 0 0',
        transitionDelay: `${estimate.timer}ms`
      }}
      {...(checked ? { timeout: 1000 } : {})}
    >
      <div className="genderContainer" style={{minHeight: "1px"}}>
        <Fade
          in={estimate.selectedHelp && estimate.helpDescription ? true : false}
          timeout={500}
        >
          <div className="helpContainer" onClick={handleHelpToggle}>
            <div className="helpBox">
              <h2 className="helpText">Tips</h2>
              {helpDescriptionLines.map((line, index) => (
                <p key={index} className="helpText">{line}</p>
              ))}
            </div>
          </div>
        </Fade>

        <div className="componentHeaderContainer">
          <div className="componentHeader">
            <div className="descriptionContainer">
              <p className="description">
                {replacedTitle}
              </p>
              <p className="note">
                {replacedBody}
              </p>
            </div>
            <div className="info">
              <ToggleButton
                value={estimate.estimate_id}
                selected={estimate.selectedHelp}
                onChange={handleHelpToggle}
                sx={{ padding: '0px', border: '0px', height: "26px", width: "26px", "&.MuiButtonBase-root.MuiToggleButton-root.Mui-selected": { backgroundColor: "rgb(45 185 255 / 17%)" } }}
              >
                <HelpIcon />
              </ToggleButton>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "row" }}>
          <div style={{ margin: "auto" }}>
            <TextField
              id="outlined-number"
              value={estimate.touched ? estimate.value : "?"}
              onClick={handleClick}
              onChange={(event) => {
                onChange({
                  ...estimate,
                  value: event.target.value
                });
              }}
              slotProps={{
                input: {
                  endAdornment:
                    <InputAdornment position="end" sx={{ margin: "0", "& .MuiTypography-root": { color: "#008FF5" } }}>
                      %
                    </InputAdornment>,
                  startAdornment: (
                    <InputAdornment position="start" disablePointerEvents={true} sx={{ marginRight: 0}}>
                      {ReplacedIcon}
                    </InputAdornment>
                  )
                },
                htmlInput: {
                  min: 0, max: 100,
                  inputMode: "numeric"
                }
              }}
              sx={{
                width: '95px', height: "40px", backgroundColor: "#FFFFFF", input: { color: "#008FF5" },
                "&.MuiTextField-root": { borderRadius: "40px" },
                "& .MuiOutlinedInput-root ": { borderRadius: "40px", height: "40px" }
              }}
            />
          </div>

          <div style={{ padding: "0 0 0 10px", margin: "auto", flex: 1 }}>
            <Slider
              value={estimate.touched ? Number(estimate.value) : 50}
              onChange={handleChange}
              valueLabelDisplay="auto"
              sx={{
                '& .MuiSlider-track': {
                  border: 'none',
                  backgroundImage: "linear-gradient(.25turn, #8ED4FF, #008FF5)",
                },
                '& .MuiSlider-thumb': {
                  height: 20,
                  width: 20,
                  backgroundColor: "white",
                  border: "2px solid #1976d2",
                  pointerEvents: 'all !important'
                },
                '&.MuiSlider-root': {
                  pointerEvents: 'none !important',
                }
              }}
            />
          </div>
        </div>
      </div>
    </Fade>
  )
}

export default function Estimates({ activeEstimates, onInput, appear }) {
  const [checked, setChecked] = useState(false);
  const [selectedHelp, setSelectedHelp] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(false);

  // const [checked, setChecked] = useState(false);
  const [firstEstimateHint, setFirstEstimateHint] = useState(true);
  const [firstEstimateHintFinished, setFirstEstimateHintFinished] = useState(false);




  // Kick off the appearance of estimate questions
  useEffect(() => {
    if (appear) setChecked(true)}
    , [appear])


  useEffect(() => {
    if (activeEstimates[0].estimate_id === 1 && activeEstimates[0].value < 100) {
      setFirstEstimateHintFinished(true)
      setFirstEstimateHint(false)
    }
}
    , [activeEstimates])





  return (
      <div className="estimateContainerParent">

        {activeEstimates.map((estimate) => (
          <EstimateQuestion key={estimate.estimate_id} estimate={estimate} onChange={onInput} checked={checked} firstEstimateHint={firstEstimateHintFinished ? false : firstEstimateHint} />

        ))}



      </div>
  );
}
